import {
  GridColDef,
  GridColType,
  GridColumnHeaderTitle,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";

import { DataType, SortBy } from "shared/types";
import { getValueByAccessor } from "shared/utils";
import {
  formatValue,
  renderBooleanIcon,
} from "shared/utils/formatValue/formatValue";

import DescribedColumnHeader from "./components/DescribedColumnHeader";
import RequiredMark from "./components/RequiredMark";
import { MIN_COLUMN_WIDTH, WIDE_COLUMN_WIDTH } from "./constants";
import type { SchemaEntry } from "./types";

export const convertSortByToMaterialSortState = (sortBy?: SortBy) =>
  Object.entries(sortBy || {}).map(([field, sort]) => ({
    field,
    sort,
  }));

export const convertMaterialSortStateToSortBy = (sortingModel: GridSortModel) =>
  sortingModel.reduce((acc, { field, sort }: any) => {
    acc[field] = sort;

    return acc;
  }, {} as SortBy);

// An underlined header carries the tooltip itself, so the grid is not given the description as well.
const getHeader = (
  { label, description, required }: SchemaEntry,
  hideTooltips: boolean,
  underlineHeaderTooltips: boolean
): Pick<GridColDef, "description" | "renderHeader"> => {
  const tooltip =
    !hideTooltips && typeof description === "string" && description
      ? description
      : undefined;

  if (tooltip && underlineHeaderTooltips) {
    return {
      renderHeader: () => (
        <DescribedColumnHeader
          label={label}
          description={tooltip}
          required={required}
        />
      ),
    };
  }

  // A custom header replaces the grid's own title, tooltip included, so a required one renders that title itself.
  if (required) {
    return {
      renderHeader: ({ colDef }) => (
        <>
          <GridColumnHeaderTitle
            label={label}
            description={tooltip}
            columnWidth={colDef.computedWidth}
          />
          <RequiredMark />
        </>
      ),
    };
  }

  return tooltip ? { description: tooltip } : {};
};

// A `formatCell` for tables where an unchecked box reads as noise: true gets a checkmark and false stays blank.
export const renderCheckOrBlankBoolean = ({
  colDef,
  value,
}: GridRenderCellParams) => {
  if (colDef.type !== "boolean") return undefined;

  return value ? renderBooleanIcon(true) : "";
};

export const convertSchemaToMaterialColumnDefinition = (
  schema: SchemaEntry[] = [],
  formatCell?: (params: GridRenderCellParams) => any,
  hideTooltips: boolean = false,
  underlineHeaderTooltips: boolean = false
) =>
  schema
    .filter((schemaEntry) => !schemaEntry.hideInTable)
    .map((schemaEntry) => ({
      field: schemaEntry.accessor,
      headerName: schemaEntry.label,
      width:
        schemaEntry.columnWidth ||
        (schemaEntry.limitedWidthClass ? WIDE_COLUMN_WIDTH : MIN_COLUMN_WIDTH),
      minWidth: schemaEntry.minWidth,
      ...getHeader(schemaEntry, hideTooltips, underlineHeaderTooltips),
      type: convertSchemaDataTypeToMaterialDataType(schemaEntry.dataType),
      filterable: !!schemaEntry.filter,
      display: schemaEntry.display,
      renderCell: renderCell(schemaEntry, formatCell),
      sortable: schemaEntry.sortable,
      flex: schemaEntry.flex,
      editable: schemaEntry.editable,
      cellClassName: schemaEntry.editable ? "cursor-pointer" : undefined,
    }));

export const convertSchemaDataTypeToMaterialDataType = (
  schemaDataType: DataType
): GridColType => {
  switch (schemaDataType) {
    case DataType.DATE:
      return "string";
    case DataType.DATE_WITH_TIME_UTC:
      return "string";
    case DataType.NUMBER:
      return "number";
    case DataType.STRING:
      return "string";
    case DataType.BOOLEAN:
      return "boolean";
    case DataType.JSX:
      return "custom";
    default:
      return "string";
  }
};

export const renderCell =
  (
    schemaEntry: SchemaEntry,
    formatCell?: (params: GridRenderCellParams) => any
  ) =>
  (params: GridRenderCellParams) => {
    const customRender = formatCell?.(params);
    if (customRender !== undefined && customRender !== null)
      return customRender;

    return formatValue({
      value: getValueByAccessor(params.field, params.row),
      dataType: schemaEntry.dataType,
      decimals: schemaEntry.decimals,
      minDecimals: schemaEntry.minDecimals,
      handleOverflow: true,
      format: schemaEntry.format,
    });
  };

interface GetDataGridSxProps {
  height?: string | number;
  skeletonHeight?: string | number;
  noTopRadius?: boolean;
  loading?: boolean;
  hasPanel?: boolean;
  headerBackgroundColor?: string;
  isRowClickable?: boolean;
  /** Pin the column-header row to the top of the nearest scrolling ancestor. */
  stickyHeader?: boolean;
  /** Pin the pagination footer to the bottom of the nearest scrolling ancestor. */
  stickyFooter?: boolean;
  /** Pixel offset for the sticky column-header row (e.g. when nested inside
   *  another sticky table whose header occupies the top of the viewport). */
  stickyTopOffset?: number;
  /** Page size — used to compute a stable minHeight so the table doesn't shrink
   *  when the last page has fewer rows. */
  pageSize?: number;
  /** Row height in px. */
  rowHeight?: number;
  /** Number of rows actually being rendered on the current page. When provided,
   *  the stable minHeight collapses to fit the visible rows on a partial page
   *  instead of reserving empty space up to `pageSize`. */
  renderedRowCount?: number;
  /** Height of the no-rows overlay area. Without it MUI falls back to two rows,
   *  which clips anything richer than a line of text. */
  overlayHeight?: number | string;
  /** Drop the grid's own surface — frame, header fill, row rules, and the
   *  hover and selection tints — for a table that reads as part of the page
   *  rather than a panel sitting on it. */
  borderless?: boolean;
}

export const getDataGridSx = ({
  height,
  loading,
  headerBackgroundColor,
  isRowClickable = false,
  stickyHeader = false,
  stickyFooter = false,
  stickyTopOffset,
  pageSize,
  rowHeight,
  renderedRowCount,
  overlayHeight,
  borderless = false,
}: GetDataGridSxProps) => {
  // Any sticky behavior requires the shared overflow overrides (so `position:
  // sticky` propagates to the page-level scroll ancestor) and the stable
  // minHeight (so the table doesn't shrink between pages).
  const anySticky = stickyHeader || stickyFooter;

  // Surfaces `borderless` clears. Pinned chrome has to stay opaque or the rows scroll through it, so whatever is
  // sticky keeps the background its own rule gives it — the header cells in front of the header container still go
  // transparent either way.
  const borderlessSurfaces = [
    "& .MuiDataGrid-columnHeader",
    "& .MuiDataGrid-topContainer",
    "& .MuiDataGrid-scrollbarFiller",
    "& .MuiDataGrid-cell",
    "& .MuiDataGrid-row",
    ...(stickyHeader
      ? []
      : ["& .MuiDataGrid-columnHeaders", "& .MuiDataGrid-filler"]),
    ...(stickyFooter ? [] : ["& .MuiDataGrid-footerContainer"]),
  ].join(", ");

  return {
    ...(overlayHeight !== undefined && {
      "--DataGrid-overlayHeight":
        typeof overlayHeight === "number"
          ? `${overlayHeight}px`
          : overlayHeight,
    }),
    maxHeight: height ?? "auto",
    height: loading ? "300px" : "auto",
    // Keep a stable minimum height sized to the rows actually rendered on the
    // current page. Only the rows region is floored — `autoHeight` already
    // adds the actual header/footer on top, so adding approximate constants
    // here overshoots the real height and leaves whitespace below the last
    // row when no toolbar absorbs the discrepancy.
    ...(anySticky &&
      !loading &&
      pageSize &&
      rowHeight && {
        minHeight: Math.min(pageSize, renderedRowCount ?? pageSize) * rowHeight,
      }),
    // Skipped under `borderless`: this selector is far more specific, so its important colour would beat the
    // transparent header below and leave the fill in place.
    ...(!borderless && {
      "&.normal-table > .MuiDataGrid-mainContent > .MuiDataGrid-main > .MuiDataGrid-virtualScroller > .MuiDataGrid-topContainer .MuiDataGrid-columnHeader, &.normal-table > .MuiDataGrid-mainContent > .MuiDataGrid-main > .MuiDataGrid-virtualScroller > .MuiDataGrid-topContainer .MuiDataGrid-filler":
        {
          backgroundColor: headerBackgroundColor
            ? `${headerBackgroundColor} !important`
            : undefined,
        },
    }),
    "&.normal-table .MuiDataGrid-row": {
      cursor: isRowClickable ? "pointer" : "default",
    },
    // Strips the grid's own surface so the rows sit on the page rather than on a panel. The grid declares its colours
    // as CSS variables from a <style> tag it renders inside itself, which lands after this rule and would win on
    // order, so the surfaces are overridden outright.
    ...(borderless && {
      backgroundColor: "transparent !important",
      border: "none !important",
      [borderlessSurfaces]: {
        backgroundColor: "transparent !important",
        borderColor: "transparent !important",
      },
      "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered, & .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover":
        {
          backgroundColor: "transparent !important",
        },
      "& .MuiDataGrid-columnSeparator": { display: "none" },
    }),
    // DataGridPro has internal containers with overflow: hidden/scroll that
    // create scroll contexts, trapping position: sticky inside the grid.
    // Override them with overflow-x: clip (clips horizontally without
    // creating a scroll context) and overflow-y: visible (lets sticky
    // propagate to the page-level scroll ancestor). MUI's native horizontal
    // scrollbar is replaced by a JS-driven one (useHorizontalOverflowScroll)
    // since overflow-x: clip disables native scrolling.
    ...(anySticky && {
      overflowX: "clip",
      overflowY: "visible",
      "& .MuiDataGrid-mainContent": { overflowX: "clip", overflowY: "visible" },
      "& .MuiDataGrid-main": { overflowX: "clip", overflowY: "visible" },
      "& .MuiDataGrid-virtualScroller": {
        overflowX: "clip !important",
        overflowY: "visible !important",
      },
      "& .MuiDataGrid-scrollbar": { display: "none !important" },
    }),
    ...(stickyHeader && {
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: headerBackgroundColor || "background.paper",
      },
      "& .MuiDataGrid-filler": {
        backgroundColor: headerBackgroundColor || "background.paper",
      },
      // The filter-panel anchor (MUI's [data-id="gridPanelAnchor"]) is
      // absolute-positioned inside .MuiDataGrid-main by default and scrolls
      // with the page, so the Popper drifts away from the sticky column
      // header as the user scrolls. Switch it to sticky so it tracks the
      // pinned header's bottom edge. The margin pair shifts the natural
      // flow position down by the header height while contributing zero
      // size to the flex column, so sibling layout is unchanged.
      "& [data-id='gridPanelAnchor']": {
        position: "sticky",
        top: `calc(${stickyTopOffset ?? 0}px + var(--DataGrid-headersTotalHeight))`,
        marginTop: "var(--DataGrid-headersTotalHeight)",
        marginBottom: "calc(-1 * var(--DataGrid-headersTotalHeight))",
      },
      // Offset the sticky column-header row when nested inside another
      // sticky table (e.g. the inner table inside a Smart Group detail
      // panel).
      ...(stickyTopOffset != null && {
        "& .MuiDataGrid-topContainer": {
          top: `${stickyTopOffset}px`,
        },
      }),
    }),
    ...(stickyFooter && {
      "& .MuiDataGrid-footerContainer": {
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        backgroundColor: headerBackgroundColor || "background.paper",
      },
    }),
  };
};
